import {getAdjustedCdnUrl} from '@aboutyou/utils';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {mediaTabletAndUp} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {getRatio} from '../../atoms/Image/helpers';
import {Ratio} from '../../atoms/Image/types';

export const BannerImage: FC<{
  headline?: string;
  subline?: string;
  url: string;
  aspectRatio?: Ratio;
  testId?: string;
  className?: string;
  /**
   * Allows to scale the image as needed. By default it will stay to the defined ratio
   */
  disableFixedRatio?: boolean;
}> = ({
  headline,
  subline,
  url,
  aspectRatio,
  disableFixedRatio,
  testId = 'bannerImage',
  className,
}) => {
  const adjustedCdnUrl = getAdjustedCdnUrl(url, {height: 500});
  return (
    <Wrapper
      data-testid={testId}
      backgroundImage={adjustedCdnUrl}
      aspectRatio={aspectRatio}
      disableFixedRatio={disableFixedRatio}
      className={className}
    >
      {headline && <Headline>{headline}</Headline>}
      {subline && <Subline>{subline}</Subline>}
    </Wrapper>
  );
};

const Wrapper = styled.section<{
  backgroundImage: string;
  aspectRatio?: Ratio;
  disableFixedRatio?: boolean;
}>`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-image: ${({backgroundImage}) => `url(${backgroundImage})`};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  ${({aspectRatio, disableFixedRatio}) =>
    !disableFixedRatio &&
    css`
      aspect-ratio: ${getAspectRatioValue(aspectRatio, '3 / 1')};
    `};

  @supports not (aspect-ratio: initial) {
    height: ${({aspectRatio}) =>
      aspectRatio ? `${getHeightFromAspectRatio(aspectRatio)}vw` : '34vw'};
    width: 100%;
  }

  ${mediaTabletAndUp} {
    ${({aspectRatio, disableFixedRatio}) =>
      !disableFixedRatio &&
      css`
        aspect-ratio: ${getAspectRatioValue(aspectRatio, '5 / 1')};
      `};

    @supports not (aspect-ratio: initial) {
      height: ${({aspectRatio}) =>
        aspectRatio ? `${getHeightFromAspectRatio(aspectRatio)}vw` : '15vw'};
      width: 100%;
    }
  }
`;

const Headline = styled.h2`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.bold};
  text-align: center;
  color: ${COLORS.White};
  margin: 0 0 0.5rem;
`;

const Subline = styled.span`
  font-size: ${FONT_SIZES.px16};
  text-align: center;
  color: ${COLORS.White};
`;

const getAspectRatioValue = (
  aspectRatio: Ratio | undefined,
  defaultValue: `${number}/${number}`,
) => {
  if (aspectRatio) {
    const {widthRatio, heightRatio} = getRatio(aspectRatio);
    return `${widthRatio} / ${heightRatio}`;
  }
  return defaultValue;
};

const getHeightFromAspectRatio = (aspectRatio: Ratio) => {
  const {widthRatio, heightRatio} = getRatio(aspectRatio);
  return (Number(heightRatio) / Number(widthRatio)) * 100;
};
